import React from "react";
import { Box, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

const ACTIVE_GREY = "#757575";
const COMPLETED_CONNECTOR_GREY = "#9E9E9E";
const PENDING_GREY = "#BDBDBD";
const PENDING_CONNECTOR_GREY = "#D8DDE3";

const AssetProgressTracker = ({
  stages,
  nodeSize = 30,
  nodeColumnWidth = 88,
  connectorHeight = 7,
}) => {
  if (!Array.isArray(stages) || stages.length === 0) {
    return null;
  }

  const currentDotSize = Math.max(10, Math.round(nodeSize * 0.46));

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        width: "100%",
        overflowX: "hidden",
        pb: 0.5,
      }}
    >
      {stages.map((stage, index) => {
        const isCompleted = stage.status === "completed" || stage.isFinalMilestoneReached;
        const isCurrent = stage.status === "in-progress" && !stage.isFinalMilestoneReached;
        const nodeColor = isCompleted || isCurrent ? ACTIVE_GREY : PENDING_GREY;
        const nextStage = stages[index + 1];
        const connectorColor =
          nextStage && nextStage.status !== "pending"
            ? COMPLETED_CONNECTOR_GREY
            : PENDING_CONNECTOR_GREY;

        return (
          <React.Fragment key={stage.key}>
            <Box
              role="listitem"
              aria-label={`${stage.title}: ${stage.statusLabel}`}
              data-status={stage.status}
              sx={{
                flex: `0 1 ${nodeColumnWidth}px`,
                minWidth: { xs: 46, sm: 62 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                data-progress-node={stage.key}
                sx={{
                  width: nodeSize,
                  height: nodeSize,
                  borderRadius: "50%",
                  border: isCompleted
                    ? "2px solid transparent"
                    : `2px solid ${nodeColor}`,
                  backgroundColor: isCompleted ? nodeColor : "#fff",
                  color: isCompleted ? "#fff" : nodeColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: isCurrent
                    ? "0 0 0 4px rgba(117, 117, 117, 0.14), 0 10px 24px rgba(117, 117, 117, 0.18)"
                    : "none",
                  position: "relative",
                }}
              >
                {isCompleted ? (
                  <CheckRoundedIcon sx={{ fontSize: Math.round(nodeSize * 0.62) }} />
                ) : isCurrent ? (
                  <Box
                    sx={{
                      width: currentDotSize,
                      height: currentDotSize,
                      borderRadius: "50%",
                      backgroundColor: nodeColor,
                    }}
                  />
                ) : null}
              </Box>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  lineHeight: 1.25,
                  textAlign: "center",
                  color: isCompleted || isCurrent ? "text.primary" : "text.secondary",
                  minHeight: 28,
                  whiteSpace: "normal",
                }}
              >
                {stage.trackerLabel}
              </Typography>
            </Box>
            {index < stages.length - 1 && (
              <Box
                sx={{
                  flex: "1 1 0",
                  minWidth: 0,
                  height: connectorHeight,
                  mt: `${Math.max(0, (nodeSize - connectorHeight) / 2)}px`,
                  mx: { xs: 0.35, sm: 0.75 },
                  borderRadius: 999,
                  backgroundColor: connectorColor,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default AssetProgressTracker;
